import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CalendarPlus, MessageCircle, Pencil, RefreshCw, Star, Trash2, Upload, AlertTriangle, CheckCircle2, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ISSUE_STATUS, ORDER_APPT_KINDS, ORDER_APPT_STATUS, ORDER_TONE, PAY_METHODS, QUOTE_CATEGORIES, fmtDateTime, fmtDay, fmtEuro } from "@/lib/format";
import { Btn, Chip, Pill, ProgressBar, Spinner } from "@/components/rp";
import { AccessData, DocItem, EmailLog, Info, InternalNote, Section, Timeline } from "@/components/commesse";
import { IssueDialog, OrderApptDialog, OrderEditDialog, OrderUpdateDialog } from "@/components/admin/orderDialogs";
import { CantiereWork } from "@/components/CantiereWork";
import { Diario } from "@/components/Diario";
import { AdminClientChecklist } from "@/components/admin/extras";
import { CantiereDocs } from "@/components/admin/cantiereDocs";
import { EdiliziaPanel } from "@/components/admin/edilizia";
import { ChatThread } from "@/components/ChatThread";
import { AdminTimeline } from "@/components/admin/adminTimeline";
import { CantiereSub } from "@/components/admin/cantiereSub";
import { Certificati, FineLavori } from "@/components/admin/fineLavori";
import { Percorso } from "@/components/admin/percorso";
import { Lavori } from "@/components/admin/lavori";
import { PrimaDopo } from "@/components/admin/primaDopo";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { SopralluoghiPanel } from "@/components/admin/sopralluoghi";

const TABS = [["panoramica", "Panoramica"], ["sopralluogo", "Sopralluogo"], ["lavori", "Lavori, SAL e materiali"], ["foto", "Foto prima e dopo"], ["diario", "Diario di bordo"], ["timeline", "Timeline amministrativa"], ["documenti", "Documenti e fatture"], ["subappalti", "Subappalti"], ["messaggi", "Messaggi"], ["edilizia", "Dati edilizi"], ["fine", "Fine lavori e garanzie"], ["certificati", "Certificati"]];

const payLine = (status, date, amount, method, okLabel) => (status ? `${okLabel} il ${fmtDay(date)} · ${fmtEuro(amount)} · ${PAY_METHODS[method] || "—"}` : "Da registrare");

const DataBox = ({ o }) => (
  <Section label="Dati cantiere" testid="order-data-section">
    <div className="grid sm:grid-cols-2 gap-4">
      <Info label="Contratto">{o.contract_number || "—"}{o.contract_date && ` · ${fmtDay(o.contract_date)}`}</Info>
      <Info label="Indirizzo del cantiere">{o.delivery_address}</Info>
      <Info label="Importo contratto">{fmtEuro(o.contract_amount)}</Info>
      <Info label="Ancora da pagare"><span data-testid="order-remaining">{fmtEuro(o.remaining_amount)}</span></Info>
      <Info label="Acconto">{payLine(o.deposit_status === "ricevuto", o.deposit_date, o.deposit_amount, o.deposit_method, "Ricevuto")}
        {o.deposit2_amount && <span className="block text-xs text-[#757575]">2° acconto {fmtEuro(o.deposit2_amount)} · {fmtDay(o.deposit2_date)}</span>}
        {o.deposit3_amount && <span className="block text-xs text-[#757575]">3° acconto {fmtEuro(o.deposit3_amount)} · {fmtDay(o.deposit3_date)}</span>}</Info>
      <Info label="Saldo">{payLine(o.payment_status === "effettuato", o.payment_date, o.payment_amount, o.payment_method, "Effettuato")}</Info>
      <Info label="Contatti cliente">{o.client_email}<span className="block">{o.client_phone}</span></Info>
      <Info label="Categoria">{QUOTE_CATEGORIES[o.category] || o.category}</Info>
    </div>
    <div className="mt-4"><AccessData x={o} /></div>
    <div className="mt-4"><InternalNote text={o.internal_notes} /></div>
  </Section>
);

const ArrivalBox = ({ o, setO }) => {
  const [f, setF] = useState({ expected_arrival_date: o.expected_arrival_date || "", actual_arrival_date: o.actual_arrival_date || "", expected_arrival_note: o.expected_arrival_note || "" });
  const save = async () => {
    try { setO((await api.post(`/orders/${o.id}/arrival`, { ...f, expected_arrival_date: f.expected_arrival_date || null, actual_arrival_date: f.actual_arrival_date || null })).data); toast.success("Date materiali salvate"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section label="Arrivo materiali" testid="order-arrival-section">
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs text-[#757575]">Arrivo previsto (indicativo)<input type="date" value={f.expected_arrival_date} onChange={(e) => setF({ ...f, expected_arrival_date: e.target.value })} className="rp-field mt-1" data-testid="arrival-expected-input" /></label>
        <label className="text-xs text-[#757575]">Arrivo effettivo (confermato)<input type="date" value={f.actual_arrival_date} onChange={(e) => setF({ ...f, actual_arrival_date: e.target.value })} className="rp-field mt-1" data-testid="arrival-actual-input" /></label>
      </div>
      <input value={f.expected_arrival_note} onChange={(e) => setF({ ...f, expected_arrival_note: e.target.value })} className="rp-field mt-3" placeholder="Intervallo / nota sull'arrivo previsto" data-testid="arrival-note-input" />
      <p className="text-xs text-[#757575] mt-2">Ogni modifica avvisa il cliente nei Messaggi.</p>
      <Btn variant="secondary" className="h-10 mt-3" onClick={save} data-testid="arrival-save-button">Salva date materiali</Btn>
    </Section>
  );
};

const UploadBtn = ({ o, setO, tag, label, testid }) => {
  const [vis, setVis] = useState("cliente");
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("visibility", tag === "progetto" ? "cliente" : vis); fd.append("tag", tag);
    try { setO((await api.post(`/orders/${o.id}/documents`, fd)).data); toast.success("Documento caricato"); } catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {tag !== "progetto" && <><Chip active={vis === "cliente"} onClick={() => setVis("cliente")} data-testid="doc-vis-cliente">Condiviso col cliente</Chip><Chip active={vis === "interno"} onClick={() => setVis("interno")} data-testid="doc-vis-interno">Interno</Chip></>}
      <label className="rp-btn border border-[#EBEBEB] bg-white hover:bg-[#F7F7F7] cursor-pointer h-10" data-testid={testid}>
        <Upload className="h-4 w-4" /> {label}
        <input type="file" accept="application/pdf,image/*" className="hidden" onChange={upload} />
      </label>
    </div>
  );
};

const DocsBox = ({ o, setO }) => {
  const toggle = async (d) => {
    try { setO((await api.patch(`/orders/${o.id}/documents/${d.id}`, { visibility: d.visibility === "cliente" ? "interno" : "cliente" })).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (d) => {
    if (!window.confirm(`Eliminare ${d.filename}?`)) return;
    try { setO((await api.delete(`/orders/${o.id}/documents/${d.id}`)).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const project = o.documents.filter((d) => d.tag === "progetto");
  return (
    <>
      <Section label="Progetto definitivo e firma del cliente" testid="order-project-section">
        {o.project_approval ? <p className="text-sm text-[#198754] mb-3 flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5" /> Firmato da {o.project_approval.by_name} il {fmtDateTime(o.project_approval.at)} (dall'Area Clienti)</p>
          : <p className="text-sm text-[#757575] mb-3">{project.length ? "In attesa della firma del cliente nell'Area Clienti." : "Carica il progetto: il cliente lo firmerà dall'Area Clienti."}</p>}
        <div className="space-y-2 mb-3">{project.map((d) => <DocItem key={d.id} d={d} href={`/api/orders/${o.id}/documents/${d.id}/file`} />)}</div>
        {!o.project_approval && <UploadBtn o={o} setO={setO} tag="progetto" label="Carica disegno / progetto" testid="order-project-upload" />}
      </Section>
      <Section label="Documenti" testid="order-docs-section">
        <div className="mb-4"><UploadBtn o={o} setO={setO} tag="" label="Carica documento" testid="order-doc-upload" /></div>
        <div className="space-y-2">
          {!o.documents.length && <p className="text-sm text-[#757575]">Nessun documento caricato.</p>}
          {o.documents.filter((d) => d.tag !== "progetto").map((d) => (
            <DocItem key={d.id} d={d} href={`/api/orders/${o.id}/documents/${d.id}/file`}>
              <button onClick={() => toggle(d)} data-testid={`order-doc-visibility-${d.id}`}><Pill tone={d.visibility === "cliente" ? "success" : "sand"}>{d.visibility === "cliente" ? "Condiviso" : "Interno"}</Pill></button>
              <button onClick={() => del(d)} className="text-[#757575] hover:text-[#DC3545]" data-testid={`order-doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
            </DocItem>
          ))}
        </div>
      </Section>
    </>
  );
};

const ApptsBox = ({ o, setO, onEdit }) => {
  const del = async (a) => {
    if (!window.confirm("Eliminare l'appuntamento?")) return;
    try { setO((await api.delete(`/orders/${o.id}/appointments/${a.id}`)).data); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section label="Appuntamenti" testid="order-appts-section">
      <div className="space-y-2">
        {!o.appointments.length && <p className="text-sm text-[#757575]">Nessun appuntamento.</p>}
        {o.appointments.map((a) => (
          <div key={a.id} className="rounded-2xl bg-white border border-[#EBEBEB] p-4" data-testid={`order-appt-${a.id}`}>
            <p className="font-medium text-sm">{ORDER_APPT_KINDS[a.kind]} — {ORDER_APPT_STATUS[a.status]}</p>
            <p className="text-sm text-[#757575]">{fmtDay(a.date)} · {a.end_time ? `fascia ${a.start_time}–${a.end_time}` : `ore ${a.start_time}`} · {a.location}</p>
            {a.instructions && <p className="text-xs text-[#757575]">Indicazioni: {a.instructions}</p>}
            <p className="text-xs mt-1">{a.client_confirmed_at ? <span className="text-[#198754]">Confermato dal cliente il {fmtDateTime(a.client_confirmed_at)}</span> : <span className="text-[#757575]">Non ancora confermato dal cliente</span>}</p>
            <div className="flex gap-3 mt-2 text-sm">
              <button onClick={() => onEdit(a)} className="underline" data-testid={`order-appt-edit-${a.id}`}>Modifica</button>
              <button onClick={() => del(a)} className="text-[#DC3545] underline" data-testid={`order-appt-delete-${a.id}`}>Elimina</button>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

const IssuesBox = ({ o, setO, onNew }) => {
  const setStatus = async (i, status) => {
    try { setO((await api.patch(`/orders/${o.id}/issues/${i.id}`, { status })).data); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section label="Problemi dopo la fine lavori" action={<Btn variant="outline" className="h-9" onClick={onNew} data-testid="order-issue-new"><AlertTriangle className="h-4 w-4" /> Nuovo</Btn>} testid="order-issues-section">
      {!o.issues.length && <p className="text-sm text-[#757575]">Nessun problema segnalato.</p>}
      {o.issues.map((i) => (
        <div key={i.id} className="rounded-2xl bg-white border border-[#EBEBEB] p-4 mb-2" data-testid={`order-issue-${i.id}`}>
          <p className="text-sm font-medium">{i.description}</p>
          <p className="text-xs text-[#757575]">{fmtDateTime(i.created_at)} · {i.created_by_name}</p>
          <div className="flex flex-wrap gap-2 mt-3">{Object.entries(ISSUE_STATUS).map(([v, l]) => <Chip key={v} active={i.status === v} onClick={() => setStatus(i, v)} data-testid={`issue-status-${i.id}-${v}`}>{l}</Chip>)}</div>
        </div>
      ))}
    </Section>
  );
};

const EventsBox = ({ o }) => (
  <Section label="Storico modifiche" testid="order-events-section">
    <div className="space-y-3 max-h-[480px] overflow-y-auto pr-2">
      {o.events.map((e) => (
        <div key={e.id} className="text-sm border-l-2 border-[#C8BDAB] pl-3">
          <p>{e.summary}</p>
          {e.public_message && <p className="text-xs mt-0.5">Al cliente: «{e.public_message}»</p>}
          {e.internal_note && <p className="text-xs mt-0.5 bg-[#E6DDCE] rounded px-2 py-1 inline-block">Interno: {e.internal_note}</p>}
          <p className="text-xs text-[#757575]">{fmtDateTime(e.created_at)} · {e.author_name}</p>
        </div>
      ))}
    </div>
  </Section>
);

const Header = ({ o, navigate, setDialog, remove }) => (
  <div className="space-y-3 rp-fade">
    <p className="rp-label">{o.client_name} · {o.code} · {QUOTE_CATEGORIES[o.category] || o.category}</p>
    <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="order-title">{o.title}</h1>
    <p className="text-sm text-[#757575]">Cliente: <Link to={`/admin/clienti/${o.client_id}`} className="text-black underline">{o.client_name}</Link> · Referente: {o.seller_name}{o.quote_id && <> · <Link to={`/admin/preventivi/${o.quote_id}`} className="underline">preventivo</Link></>}</p>
    <div className="flex flex-wrap gap-2"><Pill tone={ORDER_TONE(o.status)} data-testid="order-status">{o.status_label}</Pill><Pill tone={o.payment_status === "effettuato" ? "success" : "muted"}>Saldo: {o.payment_status === "effettuato" ? "Effettuato" : "Da effettuare"}</Pill></div>
    {o.client_confirmation && (
      <p className="text-sm text-[#198754]" data-testid="order-client-confirmation">Verbale di fine lavori firmato dal cliente ({o.client_confirmation.by_name}) il {fmtDateTime(o.client_confirmation.at)}{o.client_confirmation.note && ` — «${o.client_confirmation.note}»`} · Valutazioni: progetto {o.client_confirmation.rating_product}/5 · staff {o.client_confirmation.rating_seller}/5 · imprese {o.client_confirmation.rating_team}/5</p>
    )}
    <div className="flex flex-wrap gap-2 pt-2">
      <Btn variant="outline" onClick={() => setDialog("edit")} data-testid="order-edit-button"><Pencil className="h-4 w-4" /> Modifica dati</Btn>
      <Btn variant="outline" onClick={() => setDialog("appt")} data-testid="order-new-appt-button"><CalendarPlus className="h-4 w-4" /> Nuovo appuntamento</Btn>
      <Btn variant="outline" onClick={() => navigate(`/admin/messaggi/${o.client_id}`)} data-testid="order-chat-button"><MessageCircle className="h-4 w-4" /> Scrivi al cliente</Btn>
      <Btn variant="outline" onClick={() => navigate(`/admin/amministrazione/${o.id}`)} data-testid="order-admin-button"><Briefcase className="h-4 w-4" /> Amministrazione</Btn>
      <Btn onClick={() => setDialog("update")} data-testid="order-update-button"><RefreshCw className="h-4 w-4" /> Aggiorna cantiere</Btn>
      <Btn variant="outline" onClick={async () => { try { await api.post(`/orders/${o.id}/review-request`); toast.success("Richiesta di recensione Google inviata al cliente"); } catch (e) { toast.error(errMsg(e)); } }} data-testid="order-review-button"><Star className="h-4 w-4" /> Invia recensione</Btn>
      <Btn variant="danger" onClick={remove} data-testid="order-delete-button"><Trash2 className="h-4 w-4" /> Elimina il cantiere</Btn>
    </div>
  </div>
);

export default function AdminCommessaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "panoramica");
  const [bump, setBump] = useState(0);
  const [canSub, setCanSub] = useState(false);
  useEffect(() => { api.get("/amministrazione/settings/subappalti-access").then((r) => setCanSub(r.data.can_view)).catch(() => {}); }, []);
  useEffect(() => { api.get(`/orders/${id}`).then((r) => setO(r.data)).catch(() => navigate("/admin/commesse")); }, [id, navigate]);
  const [delOpen, setDelOpen] = useState(false);
  const remove = () => setDelOpen(true);
  const doRemove = async (confirm) => { await api.delete(`/orders/${id}`, { params: { confirm } }); toast.success("Cantiere eliminato con tutti i suoi dati"); navigate("/admin/commesse"); };
  if (!o) return <Spinner />;
  return (
    <div className="space-y-6">
      <Link to="/admin/commesse" className="inline-flex items-center gap-2 text-sm text-[#757575] hover:text-black" data-testid="back-to-orders"><ArrowLeft className="h-4 w-4" /> Cantieri</Link>
      <Header o={o} navigate={navigate} setDialog={setDialog} remove={remove} />
      <ConfirmDelete open={delOpen} onClose={() => setDelOpen(false)} title={`Eliminare il cantiere ${o.code}?`} word={o.code} testid="order-delete-dialog"
        what="il cantiere con documenti, foto, diario, pratiche, SAL e storico (il preventivo resta)" onConfirm={doRemove} />
      <Percorso oid={o.id} onTab={(t) => setTab(t)} refresh={`${tab}-${bump}`} />
      <div className="flex flex-wrap gap-2" data-testid="order-tabs">
        {TABS.filter(([k]) => k !== "subappalti" || canSub).map(([k, l]) => <Chip key={k} active={tab === k} onClick={() => setTab(k)} data-testid={`order-tab-${k}`}>{l}</Chip>)}
      </div>
      {tab === "diario" && <div className="rp-card p-5"><Diario oid={o.id} admin bare /></div>}
      {tab === "foto" && <PrimaDopo oid={o.id} />}
      {tab === "sopralluogo" && <div className="rp-card p-5 sm:p-6"><SopralluoghiPanel compact showQuote={false} filter={{ order_id: o.id }} preset={{ client_id: o.client_id, client_name: o.client_name, address: o.delivery_address || "", piano: o.delivery_floor || "", ascensore: o.elevator ?? null, ztl: o.ztl ?? null, order_id: o.id }} /></div>}
      {tab === "lavori" && <Lavori oid={o.id} onChange={() => setBump((b) => b + 1)} />}
      {tab === "timeline" && <AdminTimeline oid={o.id} />}
      {tab === "subappalti" && canSub && <CantiereSub oid={o.id} />}
      {tab === "documenti" && <CantiereDocs oid={o.id} />}
      {tab === "fine" && <FineLavori oid={o.id} />}
      {tab === "certificati" && <Certificati oid={o.id} />}
      {tab === "messaggi" && <ChatThread clientId={o.client_id} myRole="admin" />}
      {tab === "edilizia" && <EdiliziaPanel base={`/orders/${o.id}`} quote={o} onQuote={setO} note="Dati arrivati dal preventivo: aggiornali durante il cantiere. Le righe del computo assegnate a una fattura ne calcolano l'importo quando la fase viene raggiunta." />}
      {tab === "panoramica" && <>
      <div className="rp-card p-5">
        <div className="flex justify-between text-xs mb-2"><span className="rp-label">{o.status_label}</span><span className="font-semibold" data-testid="order-steps">{o.steps_done} di {o.steps_total} fasi</span></div>
        <ProgressBar value={(o.steps_done / o.steps_total) * 100} />
        <p className="text-xs text-[#757575] mt-2">{o.delivery_address}{o.expected_arrival_date && ` · Arrivo materiali previsto (indicativo) ${fmtDay(o.expected_arrival_date)}`}</p>
      </div>
      <CantiereWork cantiereId={o.cantiere_id} admin />
      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        <div className="space-y-6">
          <Section label="Prossimo appuntamento">{o.next_appointment ? <p className="text-sm">{ORDER_APPT_KINDS[o.next_appointment.kind]} · {fmtDay(o.next_appointment.date)} {o.next_appointment.start_time}</p> : <p className="text-sm text-[#757575]">Da programmare</p>}</Section>
          <Section label="Avanzamento" testid="order-progress"><Timeline steps={o.timeline} /><AdminClientChecklist oid={o.id} /></Section>
        </div>
        <div className="space-y-6">
          <DataBox o={o} />
          <ArrivalBox key={`${o.expected_arrival_date}${o.actual_arrival_date}`} o={o} setO={setO} />
          <DocsBox o={o} setO={setO} />
          <ApptsBox o={o} setO={setO} onEdit={(a) => setDialog({ appt: a })} />
          <IssuesBox o={o} setO={setO} onNew={() => setDialog("issue")} />
          <EventsBox o={o} />
          <EmailLog refId={o.id} reload={o.events.length} />
        </div>
      </div>
      </>}
      {dialog === "update" && <OrderUpdateDialog order={o} onClose={() => setDialog(null)} onSaved={setO} />}
      {dialog === "edit" && <OrderEditDialog order={o} onClose={() => setDialog(null)} onSaved={setO} />}
      {(dialog === "appt" || dialog?.appt) && <OrderApptDialog order={o} appt={dialog?.appt} onClose={() => setDialog(null)} onSaved={setO} />}
      {dialog === "issue" && <IssueDialog order={o} onClose={() => setDialog(null)} onSaved={setO} />}
    </div>
  );
}
